import { useEffect, useState } from "react";
import {
  ChevronDown,
  Copy,
  Grid3x3,
  Layers,
  MousePointerClick,
  Palette,
  Plus,
  Trash2,
  Type,
} from "lucide-react";
import { PD } from "@/constants/testIds";
import {
  BEHAVIOR_NONE,
  BEHAVIOR_OPTIONS,
  CONTENT_TYPE_BLANK,
  CONTENT_TYPE_OPTIONS,
  CONTENT_TYPE_SEARCH,
  CONTENT_TYPE_TABLE,
  GRID_COLUMNS,
  GRID_ROWS,
  GRID_SIZE_LABEL,
  applySearchContent,
  applyTableContent,
  behaviorOption,
  contentTypeOption,
  findCard,
  tryMoveCard,
  tryResizeCard,
  tryStyleCard,
} from "@/lib/pageDesignerLayout";
import { isSearchCard } from "@/lib/pageDesignerSearch";
import { isTableCard } from "@/lib/pageDesignerTable";
import PageDesignerTableBuilder, {
  PageDesignerAddContentPicker,
} from "@/components/pageDesigner/PageDesignerTableBuilder";
import PageDesignerSearchBuilder from "@/components/pageDesigner/PageDesignerSearchBuilder";

function NumberField({ id, testId, label, value, min, max, disabled, onChange }) {
  return (
    <label className="epd__field" htmlFor={id}>
      <span className="epd__label">{label}</span>
      <input
        id={id}
        data-testid={testId}
        className="epd__input"
        type="number"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const next = Number.parseInt(event.target.value, 10);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
    </label>
  );
}

function SelectField({ id, testId, label, value, disabled, options, onChange, split }) {
  return (
    <label className={`epd__field${split ? " epd__field--split" : ""}`} htmlFor={id}>
      <span className="epd__label">{label}</span>
      <select
        id={id}
        data-testid={testId}
        className="epd__select browser-default"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.status === "ready" ? option.label : `${option.label} (Coming Soon)`}
          </option>
        ))}
      </select>
    </label>
  );
}

function ComingSoonNote({ testId, option }) {
  if (!option || option.status === "ready") return null;
  return (
    <p className="epd__soon" data-testid={testId}>
      Coming Soon — {option.soonLabel}
    </p>
  );
}

function PropertySection({ number, title, icon: Icon, testId, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <details
      className="epd__section"
      data-testid={testId}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="epd__section-head">
        {Icon ? (
          <span className="epd__section-icon-wrap">
            <Icon className="epd__section-icon" aria-hidden="true" />
          </span>
        ) : null}
        <span className="epd__section-title">
          {number ? `${number}. ${title}` : title}
        </span>
        <ChevronDown className="epd__section-chevron" aria-hidden="true" />
      </summary>
      <div className="epd__section-body">{children}</div>
    </details>
  );
}

export default function PageDesignerProperties({
  cards,
  selectedId,
  panel,
  disabled,
  onPanelChange,
  onSelect,
  onCardsChange,
  onAddCard,
  onRemoveCard,
  onDuplicateCard,
}) {
  const selected = findCard(cards, selectedId);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [searchBuilderOpen, setSearchBuilderOpen] = useState(false);
  const content = contentTypeOption(selected?.contentType || CONTENT_TYPE_BLANK);
  const behavior = behaviorOption(selected?.behavior || BEHAVIOR_NONE);
  const noCard = disabled || !selected;

  useEffect(() => {
    setConfirmRemove(false);
    setPickerOpen(false);
    setBuilderOpen(false);
    setSearchBuilderOpen(false);
  }, [selectedId]);

  const applyMove = (patch) => {
    if (!selected) return;
    onCardsChange((current) => {
      const next = tryMoveCard(current, selected.id, patch.col ?? selected.col, patch.row ?? selected.row);
      return next || current;
    });
  };

  const applyResize = (patch) => {
    if (!selected) return;
    onCardsChange((current) => {
      const next = tryResizeCard(
        current,
        selected.id,
        patch.width ?? selected.width,
        patch.height ?? selected.height,
      );
      return next || current;
    });
  };

  const applyStyle = (patch) => {
    if (!selected) return;
    onCardsChange((current) => tryStyleCard(current, selected.id, patch) || current);
  };

  const closeRemove = () => setConfirmRemove(false);

  const confirmRemoveCard = () => {
    if (!selected) return;
    onRemoveCard?.(selected.id);
    setConfirmRemove(false);
  };

  return (
    <aside className="epd__panel" data-testid={PD.properties}>
      <div className="epd__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={panel === "properties"}
          data-testid={PD.propertiesTab}
          className={`epd__tab${panel === "properties" ? " is-active" : ""}`}
          onClick={() => onPanelChange("properties")}
        >
          Properties
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={panel === "layout"}
          data-testid={PD.layoutTab}
          className={`epd__tab${panel === "layout" ? " is-active" : ""}`}
          onClick={() => onPanelChange("layout")}
        >
          Layout
        </button>
      </div>

      {panel === "layout" ? (
        <div className="epd__stack" data-testid={PD.layoutStack}>
          {cards.map((card) => (
            <button
              key={card.id}
              type="button"
              className={`epd__stack-item${selectedId === card.id ? " is-selected" : ""}`}
              onClick={() => onSelect(card.id)}
            >
              <span className="epd__stack-title">{card.title}</span>
              <span className="epd__stack-meta">
                Col {card.col} · Row {card.row} · {card.width} × {card.height}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="epd__field">
            <span className="epd__label">Selected Card</span>
            <select
              id="epd-selected-card"
              data-testid={PD.selectedName}
              className="epd__select browser-default"
              value={selected ? selected.id : ""}
              onChange={(event) => onSelect(event.target.value || null)}
            >
              <option value="">None</option>
              {cards.map((card) => (
                <option key={card.id} value={card.id}>
                  {card.title}
                </option>
              ))}
            </select>
          </div>

          <div className="epd__accordion">
            <PropertySection number={1} title="Content" icon={Type} testId={PD.sectionContent}>
              <SelectField
                id="epd-content-type"
                testId={PD.contentType}
                label="Content Type"
                value={selected?.contentType || CONTENT_TYPE_BLANK}
                disabled={noCard}
                options={CONTENT_TYPE_OPTIONS}
                split
                onChange={(contentType) => {
                  applyStyle({ contentType });
                  setPickerOpen(false);
                  setBuilderOpen(contentType === CONTENT_TYPE_TABLE);
                  setSearchBuilderOpen(contentType === CONTENT_TYPE_SEARCH);
                }}
              />
              <div className="epd__manage" style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  data-testid={PD.addContentBtn}
                  className="epd__btn"
                  disabled={noCard}
                  onClick={() => {
                    setPickerOpen(true);
                    setBuilderOpen(false);
                    setSearchBuilderOpen(false);
                  }}
                >
                  Add Content
                </button>
                {selected && isTableCard(selected) ? (
                  <button
                    type="button"
                    data-testid={PD.configureTableBtn}
                    className="epd__btn epd__btn--ghost"
                    disabled={noCard}
                    onClick={() => {
                      setBuilderOpen(true);
                      setPickerOpen(false);
                    }}
                  >
                    Configure Table
                  </button>
                ) : null}
                {selected && isSearchCard(selected) ? (
                  <button
                    type="button"
                    data-testid={PD.configureSearchBtn}
                    className="epd__btn epd__btn--ghost"
                    disabled={noCard}
                    onClick={() => {
                      setSearchBuilderOpen(true);
                      setPickerOpen(false);
                    }}
                  >
                    Configure Search
                  </button>
                ) : null}
              </div>
              <p className="epd__help">
                The card is the container. Add Content chooses the functionality that renders inside
                it.
              </p>
              <ComingSoonNote testId={PD.contentSoon} option={selected ? content : null} />
            </PropertySection>

            <PropertySection number={2} title="Behavior" icon={MousePointerClick} testId={PD.sectionBehavior}>
              <SelectField
                id="epd-behavior"
                testId={PD.behavior}
                label="Behavior"
                value={selected?.behavior || BEHAVIOR_NONE}
                disabled={noCard}
                options={BEHAVIOR_OPTIONS}
                split
                onChange={(nextBehavior) => applyStyle({ behavior: nextBehavior })}
              />
              <p className="epd__help">Define what happens when this card is clicked or interacted with.</p>
              <ComingSoonNote testId={PD.behaviorSoon} option={selected ? behavior : null} />
            </PropertySection>

            <PropertySection number={3} title="Position (Grid)" icon={Grid3x3} testId={PD.sectionPosition}>
              <div className="epd__pair">
                <NumberField
                  id="epd-col"
                  testId={PD.posCol}
                  label="Column"
                  value={selected ? String(selected.col) : ""}
                  min={1}
                  max={GRID_COLUMNS}
                  disabled={noCard}
                  onChange={(col) => applyMove({ col })}
                />
                <NumberField
                  id="epd-row"
                  testId={PD.posRow}
                  label="Row"
                  value={selected ? String(selected.row) : ""}
                  min={1}
                  max={GRID_ROWS}
                  disabled={noCard}
                  onChange={(row) => applyMove({ row })}
                />
              </div>
              <div className="epd__pair">
                <NumberField
                  id="epd-width"
                  testId={PD.sizeWidth}
                  label="Width"
                  value={selected ? String(selected.width) : ""}
                  min={1}
                  max={GRID_COLUMNS}
                  disabled={noCard}
                  onChange={(width) => applyResize({ width })}
                />
                <NumberField
                  id="epd-height"
                  testId={PD.sizeHeight}
                  label="Height"
                  value={selected ? String(selected.height) : ""}
                  min={1}
                  max={GRID_ROWS}
                  disabled={noCard}
                  onChange={(height) => applyResize({ height })}
                />
              </div>
              <p className="epd__help">
                Position and size are always whole cells of the {GRID_SIZE_LABEL}. A card can
                never be placed or sized past the edge of the page.
              </p>
            </PropertySection>

            <PropertySection number={4} title="Card Management" icon={Layers} testId={PD.sectionManage}>
              <div className="epd__manage">
                <button
                  type="button"
                  data-testid={PD.addCardBtnPanel}
                  className="epd__btn"
                  disabled={disabled}
                  onClick={onAddCard}
                >
                  <Plus className="epd__btn-icon" aria-hidden="true" />
                  Add Card
                </button>
                <button
                  type="button"
                  data-testid={PD.removeCardBtn}
                  className="epd__btn epd__btn--danger"
                  disabled={noCard}
                  onClick={() => selected && setConfirmRemove(true)}
                >
                  <Trash2 className="epd__btn-icon" aria-hidden="true" />
                  Remove Card
                </button>
                <button
                  type="button"
                  data-testid={PD.duplicateCardBtn}
                  className="epd__btn epd__btn--ghost"
                  disabled={noCard}
                  onClick={() => selected && onDuplicateCard?.(selected.id)}
                >
                  <Copy className="epd__btn-icon" aria-hidden="true" />
                  Duplicate
                </button>
              </div>
              <p className="epd__tip">
                Tip: Drag the card on the grid to move it. Use the resize handle in the
                bottom-right corner to change its size.
              </p>
            </PropertySection>

            <PropertySection
              title="Style"
              icon={Palette}
              testId={PD.sectionStyle}
              defaultOpen={false}
            >
              <div className="epd__pair">
                <label className="epd__field" htmlFor="epd-bg">
                  <span className="epd__label">Background</span>
                  <div className="epd__color-row">
                    <input
                      id="epd-bg"
                      data-testid={PD.styleBackground}
                      className="epd__color"
                      type="color"
                      value={selected?.background || "#ffffff"}
                      disabled={noCard}
                      onChange={(event) => applyStyle({ background: event.target.value })}
                    />
                    <span className="epd__readonly">{selected ? selected.background : "—"}</span>
                  </div>
                </label>
                <label className="epd__field" htmlFor="epd-border">
                  <span className="epd__label">Border</span>
                  <div className="epd__color-row">
                    <input
                      id="epd-border"
                      data-testid={PD.styleBorder}
                      className="epd__color"
                      type="color"
                      value={selected?.border || "#ffffff"}
                      disabled={noCard}
                      onChange={(event) => applyStyle({ border: event.target.value })}
                    />
                    <span className="epd__readonly">{selected ? selected.border : "—"}</span>
                  </div>
                </label>
              </div>
            </PropertySection>
          </div>

          <div className="epd__note">
            <h3>V1 Prototype</h3>
            <p>
              Cards are layout containers on a {GRID_SIZE_LABEL}. Table and Search content read
              existing EMRbyMD data sources; they do not create database tables.
            </p>
          </div>
        </>
      )}

      <PageDesignerAddContentPicker
        open={Boolean(pickerOpen && selected && !disabled)}
        onClose={() => setPickerOpen(false)}
        onChooseTable={() => {
          setPickerOpen(false);
          setBuilderOpen(true);
        }}
        onChooseSearch={() => {
          setPickerOpen(false);
          setSearchBuilderOpen(true);
        }}
      />
      <PageDesignerTableBuilder
        card={selected}
        open={Boolean(builderOpen && selected && !disabled)}
        onClose={() => setBuilderOpen(false)}
        onSave={(tableContent) => {
          if (!selected) return;
          onCardsChange((current) => applyTableContent(current, selected.id, tableContent) || current);
          setBuilderOpen(false);
        }}
      />
      <PageDesignerSearchBuilder
        card={selected}
        open={Boolean(searchBuilderOpen && selected && !disabled)}
        onClose={() => setSearchBuilderOpen(false)}
        onSave={(searchContent) => {
          if (!selected) return;
          onCardsChange(
            (current) => applySearchContent(current, selected.id, searchContent) || current,
          );
          setSearchBuilderOpen(false);
        }}
      />

      {confirmRemove && selected && (
        <div className="epd__modal" role="dialog" aria-modal="true" data-testid={PD.removeDialog}>
          <div className="epd__modal-card">
            <h3 data-testid={PD.removeDialogTitle}>{`Remove ${selected.title}?`}</h3>
            <p>This will remove the card from the layout.</p>
            <div className="epd__modal-actions">
              <button
                type="button"
                className="epd__btn epd__btn--ghost"
                data-testid={PD.removeCancel}
                onClick={closeRemove}
              >
                Cancel
              </button>
              <button
                type="button"
                className="epd__btn epd__btn--danger-solid"
                data-testid={PD.removeConfirm}
                onClick={confirmRemoveCard}
              >
                Remove Card
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
